/**
 * Parsed model for a Mermaid `sequenceDiagram`.
 *
 * Mirrors the typed-model approach used by the other hand-rolled Mermaid
 * renderers (timeline, flowchart). Discriminants are enums rather than string
 * literals so comparisons satisfy the repo's `local-rules/no-loose-string`
 * lint rule (see flowchart's `FlowchartNodeShape` / `FlowchartEdgeType`).
 */

/** Arrow flavor for a message line, derived from the Mermaid arrow token. */
export enum SequenceArrowKind {
  /** Solid line, open arrowhead — `A->B`. */
  SolidOpen = 'solid-open',
  /** Solid line, filled arrowhead — `A->>B`. */
  Solid = 'solid',
  /** Dashed line, open arrowhead — `A-->B`. */
  DashedOpen = 'dashed-open',
  /** Dashed line, filled arrowhead — `A-->>B` (typically a reply/return). */
  Dashed = 'dashed',
}

/** Placement of a `Note` annotation relative to its participant(s). */
export enum SequenceNotePlacement {
  Over = 'over',
  Left = 'left',
  Right = 'right',
}

/** Discriminator for the ordered event union. */
export enum SequenceEventKind {
  Message = 'message',
  Note = 'note',
  Block = 'block',
}

/** A participant (or actor) lane, in left-to-right declaration order. */
export interface MermaidSequenceParticipant {
  /** Canonical id used to match messages (the token before any `as` alias). */
  id: string;
  /** Human label shown in the lane header (alias if given, else the id). */
  label: string;
  /** `actor` lanes render with a stick-figure affordance; `participant` a box. */
  isActor: boolean;
}

/** A single message exchanged between two participants, in source order. */
export interface MermaidSequenceMessage {
  kind: SequenceEventKind.Message;
  from: string;
  to: string;
  arrow: SequenceArrowKind;
  text: string;
}

/** A `Note over A[,B]: ...` / `Note left of A: ...` annotation. */
export interface MermaidSequenceNote {
  kind: SequenceEventKind.Note;
  /** Participant ids the note spans (1 for left/right-of, 1-2 for over). */
  participants: string[];
  placement: SequenceNotePlacement;
  text: string;
}

/**
 * A grouping block (`loop` / `alt` / `opt` / `par`). We keep the label and the
 * ordered child events so the renderer can draw a labelled frame around them.
 * Unsupported sub-syntax inside a block degrades to plain messages rather than
 * throwing.
 */
export interface MermaidSequenceBlock {
  kind: SequenceEventKind.Block;
  /** The opening keyword, e.g. `loop`, `alt`, `opt`, `par`. */
  type: string;
  /** Title text after the keyword (may be empty). */
  label: string;
  events: MermaidSequenceEvent[];
}

export type MermaidSequenceEvent =
  | MermaidSequenceMessage
  | MermaidSequenceNote
  | MermaidSequenceBlock;

export interface MermaidSequence {
  /** Optional diagram title (`title ...`). */
  title?: string;
  participants: MermaidSequenceParticipant[];
  events: MermaidSequenceEvent[];
}
